import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X } from 'lucide-react';
import { useOfferte } from '../context/OfferteContext';
import { useLang } from '../context/LanguageContext';

const Navbar = () => {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openOfferte } = useOfferte();
  const { lang, c, ui, lp, otherLangPath } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  const homePath = lp('home');

  const LangSwitch = ({ className = '' }) => (
    <div className={`flex items-center gap-2 text-[13px] ${className}`} data-testid="lang-switch">
      <Link
        to={otherLangPath('nl')}
        data-testid="lang-nl"
        className={`px-2 py-0.5 rounded ${lang === 'nl' ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white'}`}
      >
        NL
      </Link>
      <span className="text-white/20">/</span>
      <Link
        to={otherLangPath('en')}
        data-testid="lang-en"
        className={`px-2 py-0.5 rounded ${lang === 'en' ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white'}`}
      >
        EN
      </Link>
    </div>
  );

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${scrolled ? 'bg-[#0a0a0b] border-b border-white/5' : 'bg-transparent'}`}>
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 h-24 flex items-center justify-between">
        <Link to={homePath} className="flex items-center group">
          <img
            src="https://customer-assets-jt897jd0.emergentagent.net/job_shiny-clone/artifacts/si4fwday_logo%20shiny.jpg"
            alt="Shiny-Cleaning"
            className="h-16 md:h-20 w-auto object-contain transition-transform group-hover:scale-[1.03]"
            style={{
              mixBlendMode: 'screen',
              filter: 'grayscale(100%) brightness(3) contrast(1.15)',
            }}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {c.nav.map(item => {
            const to = lp(item.key);
            const active = item.key === 'home' ? pathname === to : pathname.startsWith(to);
            return (
              <Link
                key={item.key}
                to={to}
                className={`text-[14px] tracking-wide transition-colors ${active ? 'text-white' : 'text-white/60 hover:text-white'}`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-5">
          <LangSwitch className="hidden md:flex" />
          <a href={c.site.phoneHref} className="hidden md:flex items-center gap-2 text-white/80 hover:text-white text-[13px] transition-colors">
            <Phone className="w-4 h-4 text-[#2f6bff]" />
            {c.site.phone}
          </a>
          <button
            onClick={() => openOfferte()}
            data-testid="navbar-offerte-btn"
            className="hidden sm:inline-flex items-center px-5 py-2.5 rounded-md bg-[#2f6bff] hover:bg-[#1f56e8] text-white text-[13px] font-medium transition-colors"
          >
            {ui.navOfferte}
          </button>
          <button className="lg:hidden text-white" onClick={() => setOpen(v => !v)} aria-label="Menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0a0a0b] border-t border-white/5 px-6 py-6 space-y-4">
          {c.nav.map(item => (
            <Link key={item.key} to={lp(item.key)} className="block text-white/80 text-base">{item.label}</Link>
          ))}
          <LangSwitch className="pt-2" />
          <button onClick={() => { setOpen(false); openOfferte(); }} data-testid="navbar-mobile-offerte-btn" className="inline-flex items-center px-5 py-2.5 rounded-md bg-[#2f6bff] text-white text-sm">{ui.navOfferte}</button>
        </div>
      )}
    </header>
  );
};

export default Navbar;
